import {
  initialState,
  Iorder,
  Iorder_by,
  ProductFiltersI,
} from '@/components/Form/ProductsFilter/productsFilterSlice';
import { omitBy } from 'lodash-es';
import {
  IProdView,
  ApiProductsResponse,
  IndustryCategory,
  Category,
  Country,
  Manufacturer,
  AccountManager,
} from '../types/bevmaq.types';
import accountManagersJSON from '@/lib/data/account_managers';

export const DEFAULT_PAGE_SIZE = 12;

export async function getProductsByForm(form: ProductFiltersI) {
  return await getProducts({
    countries: form.countries ? form.countries?.join(',') : '',
    locale: form.locale || 'en-US',
    categories: form.selectedCategory ? form.selectedCategory.join(',') : '',
    industry_categories: form.selectedIndustryCategory
      ? form.selectedIndustryCategory.join(',')
      : '',
    manufacturers: form.manufacturers ? form.manufacturers.join(',') : '',
    min_year: form.minYear?.toString() || '',
    max_year: form.maxYear?.toString() || '',
    search: form.search || '',
    pageSize: DEFAULT_PAGE_SIZE,
    page: form.page,
    min_price: form.minPrice?.toString() || '',
    max_price: form.maxPrice?.toString() || '',
    order: form.order || initialState.order,
    order_by: form.order_by || initialState.order_by,
  });
}
/* eslint-disable unused-imports/no-unused-vars */
export async function getProducts(
  {
    pageSize = DEFAULT_PAGE_SIZE,
    locale = 'en-US',
    manufacturers = '',
    categories = '',
    industry_categories = '',
    min_year = '',
    max_year = '',
    min_price = '',
    max_price = '',
    search = '',
    countries = '',
    page = 1,
    order = initialState.order,
    order_by = initialState.order_by,
  }: {
    pageSize?: number;
    locale?: string;
    manufacturers?: string;
    categories?: string;
    industry_categories?: string;
    min_year?: string;
    max_year?: string;
    min_price?: string;
    max_price?: string;
    search?: string;
    countries?: string;
    page?: number;
    order?: Iorder;
    order_by?: Iorder_by;
  } = {
    pageSize: DEFAULT_PAGE_SIZE,
    locale: 'en-US',
    manufacturers: '',
    categories: '',
    industry_categories: '',
    max_year: '',
    min_price: '',
    max_price: '',
    search: '',
    countries: '',
  }
): Promise<ApiProductsResponse> {
  const filters: Record<string, string> = {
    format: 'json',
    categories: categories || '',
    industry_categories: industry_categories || '',
    min_year: min_year || '',
    max_year: max_year || '',
    countries: countries || '',
    manufacturers: manufacturers || '',
    min_price: min_price?.toString() || '',
    max_price: max_price?.toString() || '',
    search: search || '',
    limit: pageSize?.toString() || '',
    offset:
      ((pageSize || DEFAULT_PAGE_SIZE) * ((page || 1) - 1)).toString() || '',
    order: order || '',
    order_by: order_by || '',
  };
  const filtersClean = omitBy(filters, (i) => i == '');
  const params = new URLSearchParams(filtersClean);
  const url =
    process.env.NEXT_PUBLIC_EXTERNAL_API_URL + '/v1/?' + params.toString();
  const res = await fetch(url, {
    headers: {
      accept: 'application/json, text/plain, */*',
      'accept-language': locale || 'en-US',
    },
  });
  if (!res.ok) {
    // Throw an error if the response status is not OK
    // log all
    console.log('Api error: Req -', url);
    console.log('Api error: Response status -', res.status);
    console.log('Api error:  Response headers:', res.headers);
    console.log('Api error:  Response body:', await res.text());
    throw new Error(`Error fetching products: ${res.statusText}`);
  }

  // Check if the content-type is JSON
  if (!res.headers.get('content-type')?.includes('application/json')) {
    console.log('Api not json: Response status:', res.status);
    console.log('Api not json:Response headers:', res.headers);
    throw new Error('Received non-JSON response from the server');
  }

  return await res.json();
}

export async function getProduct(
  slug: string,
  locale = 'en-US'
): Promise<IProdView> {
  const res = await fetch(
    process.env.NEXT_PUBLIC_EXTERNAL_API_URL + '/v1/' + slug + '?format=json',
    {
      headers: {
        accept: 'application/json, text/plain, */*',
        'accept-language': locale,
      },
    }
  );
  return await res.json();
}

export async function getAccountManagerByLocale(
  tld = 'com'
): Promise<AccountManager> {
  return accountManagersJSON[tld];
  // API cannot handle one request per product page yet
  const res = await fetch(
    process.env.NEXT_PUBLIC_CRM_API_URL + '/account-manager/?locale=' + tld,
    {
      headers: {
        accept: 'application/json, text/plain, */*',
        'accept-language': 'en',
      },
    }
  );
  const accountManagersList = await res.json();
  return accountManagersList[0];
}

export async function getManufacturers(
  locale = 'en-US'
): Promise<Manufacturer[]> {
  const res = await fetch(
    process.env.NEXT_PUBLIC_EXTERNAL_API_URL +
      '/v1/manufacturers/' +
      '?format=json',
    {
      headers: {
        accept: 'application/json, text/plain, */*',
        'accept-language': locale,
      },
    }
  );
  return await res.json();
}

export async function getRelatedProducts(
  slug: string,
  locale = 'en-US'
): Promise<ApiProductsResponse> {
  const res = await fetch(
    process.env.NEXT_PUBLIC_EXTERNAL_API_URL +
      '/v1/' +
      slug +
      '/related?format=json',
    {
      headers: {
        accept: 'application/json, text/plain, */*',
        'accept-language': locale,
      },
    }
  );
  return await res.json();
}

export async function getIndustryCategories(
  locale: string | undefined
): Promise<IndustryCategory[]> {
  const resp = await fetch(
    process.env.NEXT_PUBLIC_EXTERNAL_API_URL + '/v1/industry_categories',
    {
      headers: {
        accept: 'application/json, text/plain, */*',
        'accept-language': locale ?? 'en-US,en;q=0.9',
        'content-type': 'application/json',
      },
      method: 'GET',
      mode: 'cors',
    }
  );
  return await resp.json();
}

export async function getCategories(
  locale: string | undefined,
  industryCategoryName?: string
): Promise<Category[]> {
  const queryParam = industryCategoryName
    ? `?industry_category=${encodeURIComponent(industryCategoryName)}`
    : '';

  const resp = await fetch(
    process.env.NEXT_PUBLIC_EXTERNAL_API_URL + '/v1/categories' + queryParam,
    {
      headers: {
        accept: 'application/json, text/plain, */*',
        'accept-language': locale ?? 'en-US,en;q=0.9',
        'content-type': 'application/json',
      },
      method: 'GET',
      mode: 'cors',
    }
  );
  return await resp.json();
}
export async function getCountries(
  locale: string | undefined
): Promise<Country[]> {
  const resp = await fetchWithRetry(
    process.env.NEXT_PUBLIC_EXTERNAL_API_URL + '/v1/countries/',
    {
      headers: {
        accept: 'application/json, text/plain, */*',
        'accept-language': locale ?? 'en-US,en;q=0.9',
      },
      method: 'GET',
    }
  );
  return await resp.json();
}

export function getSuggestions(locale: string, q: string) {
  return getProducts({ locale, search: q });
}

// fetch with retry on error
export async function fetchWithRetry(
  url: string,
  options: RequestInit,
  n = 3
): Promise<Response> {
  try {
    return await fetch(url, options);
  } catch (err) {
    if (n === 1) throw err;
    return await fetchWithRetry(url, options, n - 1);
  }
}
